import {
    Flex,
    Text,
    Skeleton,
    Box,
    useColorModeValue,
    Spinner,
    Thead,
    Table,
    Tr,
    Th,
    Tbody,
    Td,
    useToast,
    Icon,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    HStack,
    Badge,
} from "@chakra-ui/react";
import StationsApi from "Api/StationsApi";
import { useLoading } from "Providers/LoadingProvider";
import { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import StationMap from "./Components/StationMap";
import jsonData from "../../apex_chart_pt_br.json";
import { useParams, useHistory } from "react-router-dom";
import moment from "moment";
import {
    TbArrowDown,
    TbArrowUp,
    TbCloudRain,
    TbDropCircle,
    TbDroplets,
    TbWind,
    TbWindsock,
    TbWindmill,
    TbSunHigh,
    TbArrowBigDownLines,
    TbBattery3,
    TbSpray,
    TbCircleCheckFilled,
    TbCircleXFilled,
    TbCircleDotFilled,
    TbAlertTriangleFilled,
    TbLeaf,
    TbFlame,
    TbWifiOff,
    TbMapPin,
    TbChartLine,
    TbTable,
    TbTemperature,
    TbCloudQuestion,
    TbBroadcast,
    TbSteam,
} from "react-icons/tb";
import Forecast16Graphs from "views/Components/Forecast16Graphs";

export default function StationDetails() {
    let { id } = useParams();
    const history = useHistory();
    const toast = useToast({
        position: "top-right",
    });

    const bgColor = useColorModeValue("white", "gray.800");
    const cardBg = useColorModeValue("gray.50", "gray.700");
    const borderColor = useColorModeValue("gray.200", "gray.600");
    const subtleText = useColorModeValue("gray.600", "gray.400");

    // Temperature card colors
    const minTempBg = useColorModeValue("blue.50", "blue.900");
    const minTempBorder = useColorModeValue("blue.100", "blue.700");
    const minTempLabel = useColorModeValue("blue.500", "blue.300");
    const minTempValue = useColorModeValue("blue.600", "blue.200");

    const currTempBg = useColorModeValue("brand.50", "brand.900");
    const currTempBorder = useColorModeValue("brand.200", "brand.600");
    const currTempValue = useColorModeValue("brand.600", "brand.200");

    const maxTempBg = useColorModeValue("red.50", "red.900");
    const maxTempBorder = useColorModeValue("red.100", "red.700");
    const maxTempLabel = useColorModeValue("red.500", "red.300");
    const maxTempValue = useColorModeValue("red.500", "red.300");

    // Table colors
    const tableHeaderBg = useColorModeValue("gray.100", "gray.600");

    const degreesToCardinal = (dir) => {
        if (dir == null) return "-";
        let directions = [
            "N",
            "NNE",
            "NE",
            "ENE",
            "E",
            "ESE",
            "SE",
            "SSE",
            "S",
            "SSW",
            "SW",
            "WSW",
            "W",
            "WNW",
            "NW",
            "NNW",
            "N",
        ];
        return directions[Math.round(dir / 22.5)];
    };

    const [data, setData] = useState([]);
    const [data24, setData24] = useState([]);
    const [station, setStation] = useState(null);
    const [currData, setCurrData] = useState(null);
    const [extremos, setExtremos] = useState(null);
    const [vpdExtremes, setVpdExtremes] = useState(null);
    const [last10Data, setLast10Data] = useState([]);
    const [pulvData, setPulvData] = useState({});
    const [fireRating, setFireRating] = useState(null);
    const [offlineSince, setOfflineSince] = useState(null);
    const [extraFields, setExtraFields] = useState([]);
    const [extraData24, setExtraData24] = useState([]);
    const [extraData7d, setExtraData7d] = useState([]);
    const offlineModal = useDisclosure();

    // const [forecastData, setForecastData] = useState([]);
    const loading = useLoading();

    // Check if station is offline (no data in last 5 hours)
    const checkOfflineStatus = (stationData, currentData) => {
        if (!stationData || !currentData) return;

        const lastUpdate = currentData.created_at || stationData.last_sync_at;
        if (!lastUpdate) return;

        const lastUpdateMoment = moment(lastUpdate);
        const hoursSinceUpdate = moment().diff(lastUpdateMoment, 'hours');

        // Station is considered offline if no data in last 5 hours
        if (hoursSinceUpdate >= 5) {
            setOfflineSince(lastUpdateMoment);
            offlineModal.onOpen();
        }
    };

    async function loadData() {
        // setForecastData([]);
        let response = null;
        try {
            response = await StationsApi.detail(id);
        } catch (err) { }

        if (!response || response.status != 200 || !response.json.station) {
            toast({
                status: "error",
                title: `Estação com ID #${id} não encontrada!`,
                duration: 6000,
                variant: "solid",
                isClosable: true,
            });
            history.push("/");
            return;
        }

        let _station = response.json.station;

        setData(response.json.data_7_days);
        setData24(response.json.data_24_hours);
        setCurrData({ ...response.json.curr_data, eto: response.json?.eto });
        setLast10Data(response.json.last_10_entries);
        setExtremos({
            minima: response.json.minima,
            maxima: response.json.maxima,
        });
        setVpdExtremes(response.json.vpd_extremes || null);
        setStation(_station);
        setFireRating(response.json.fwi);
        setExtraFields(response.json.extra_fields || []);
        setExtraData24(response.json.extra_data_24_hours || []);
        setExtraData7d(response.json.extra_data_7_days || []);

        setPulvData(calculatePulv(response.json.curr_data));

        // Check offline status only on first load
        if (!station) {
            checkOfflineStatus(_station, response.json.curr_data);
        }

        window.gtag("event", "visualizacao_estacao", {
            event_label: "Visualização Estação",
            value: _station.identifier + " - " + _station.provider,
        });

        setTimeout(() => {
            loading.hide();
        }, 500);
        // response = await StationsApi.forecast(_station.id);
        // setForecastData(response.json);
    }

    useEffect(() => {
        loading.show();
        refreshData();

        return () => {
            if (window.timeoutRefreshDetailsData) {
                clearTimeout(window.timeoutRefreshDetailsData);
            }
        }
    }, []);

    async function refreshData() {
        loadData();

        window.timeoutRefreshDetailsData = setTimeout(() => {
            refreshData();
        }, 1000 * 60 * 2);
    }

    const getChartSeriesTemp = () => [
        {
            name: "Temperatura Mínima",
            data: data.map((d) => parseFloat(d.temp_min)) || [],
        },
        {
            name: "Temperatura Média",
            data: data.map((d) => parseFloat(d.temp_avg)) || [],
        },
        {
            name: "Temperatura Máxima",
            data: data.map((d) => parseFloat(d.temp_max)) || [],
        },
    ];

    const getChartSeriesPrecip = () => [
        {
            name: "Precipitação",
            data: data?.map((d) => parseFloat(d.precip_total || 0)) || [],
        },
    ];

    const getChartSeries24Temp = () => [
        {
            name: "Temperatura",
            data: data24?.map((d) => parseFloat(d.temp_avg)) || [],
            color: "#ECC94B",
        },
    ];

    const hasVpd24 = data24?.some((d) => d.vpd_avg != null);

    const getChartSeries24Vpd = () => [
        {
            name: "VPD",
            data: data24?.map((d) => (d.vpd_avg != null ? parseFloat(d.vpd_avg) : null)) || [],
            color: "#319795",
        },
    ];

    const getChartSeries24Precip = () => [
        {
            name: "Precipitação",
            data: data24?.map((d) => parseFloat(d.precip_total) || 0) || [],
        },
    ];

    const getChartOptions24 = (suffix = "", dataLabels = true) => {
        return {
            chart: {
                toolbar: {
                    show: false,
                },
                locales: [jsonData],
                defaultLocale: "pt-br",
            },
            tooltip: {
                theme: "dark",
                x: {
                    show: true,
                    format: "dd MMM HH:mm",
                },
            },
            dataLabels: {
                enabled: dataLabels,
                formatter: function (val, opts) {
                    if (val == null || opts.dataPointIndex == 0 || opts.dataPointIndex % 20 != 0)
                        return "";

                    return val + suffix;
                },
            },
            xaxis: {
                type: "datetime",
                categories: data24.map((d) => d.date) || [],
                show: true,
                color: "#A0AEC0",
                labels: {
                    show: true,
                    datetimeUTC: false,
                    style: {
                        colors: "#A0AEC0",
                        fontSize: "14px",
                    },
                },
            },
            yaxis: {
                show: true,
                color: "#A0AEC0",
                labels: {
                    show: true,
                    style: {
                        colors: "#A0AEC0",
                        fontSize: "14px",
                    },
                },
            },
        };
    };

    const getChartOptions = (suffix = "") => {
        return {
            chart: {
                toolbar: {
                    show: false,
                },
                locales: [jsonData],
                defaultLocale: "pt-br",
            },
            tooltip: {
                theme: "dark",
                x: {
                    show: true,
                },
            },
            dataLabels: {
                enabled: true,
                formatter: function (val, opts) {
                    return val + suffix;
                },
            },
            xaxis: {
                type: "datetime",
                categories: data?.map((d) => d.date) || [],
                show: true,
                color: "#A0AEC0",
                labels: {
                    show: true,
                    datetimeUTC: true,
                    style: {
                        colors: "#A0AEC0",
                        fontSize: "14px",
                    },
                },
            },
            yaxis: {
                show: true,
                color: "#A0AEC0",
                labels: {
                    show: true,
                    style: {
                        colors: "#A0AEC0",
                        fontSize: "14px",
                    },
                },
            },
        };
    };


    if (station == null || loading.loading) {
        return (
            <Box p={{ base: 3, md: 6 }} maxW={"8xl"} mx="auto">
                <Box
                    bg={bgColor}
                    borderRadius="2xl"
                    shadow="xl"
                    overflow="hidden"
                    border="1px"
                    borderColor={borderColor}
                >
                    {/* Header skeleton */}
                    <Box
                        bg="linear-gradient(135deg, #2D6B3F 0%, #1F5A2F 100%)"
                        py={6}
                        px={6}
                    >
                        <Flex align="center" justify="center" direction="column" gap={3}>
                            <Icon as={TbMapPin} boxSize={8} color="white" opacity={0.8} />
                            <Text color="white" fontSize="lg" fontWeight="medium">
                                Carregando dados da estação...
                            </Text>
                        </Flex>
                    </Box>
                    <Box p={8}>
                        <Flex justify="center" align="center" direction="column" gap={4}>
                            <Spinner size="xl" color="brand.500" thickness="4px" />
                            <Text color={subtleText} fontSize="sm">
                                Aguarde enquanto buscamos os dados...
                            </Text>
                        </Flex>
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box p={{ base: 3, md: 6 }} maxW={"8xl"} mx="auto">
            {/* Offline Station Modal */}
            <Modal isOpen={offlineModal.isOpen} onClose={offlineModal.onClose} isCentered size="md">
                <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
                <ModalContent borderRadius="2xl" overflow="hidden" shadow="2xl">
                    <ModalHeader
                        bg="orange.500"
                        color="white"
                        py={4}
                    >
                        <Flex align="center" gap={3}>
                            <Flex
                                align="center"
                                justify="center"
                                bg="whiteAlpha.200"
                                borderRadius="lg"
                                p={2}
                            >
                                <Icon as={TbWifiOff} boxSize={5} />
                            </Flex>
                            <Box>
                                <Text fontSize="lg" fontWeight="bold">Estação Offline</Text>
                                <Text fontSize="xs" fontWeight="normal" opacity={0.9}>
                                    Sem comunicação recente
                                </Text>
                            </Box>
                        </Flex>
                    </ModalHeader>
                    <ModalCloseButton color="white" _hover={{ bg: "whiteAlpha.200" }} />
                    <ModalBody py={5} px={5} bg={bgColor}>
                        <Alert
                            status="warning"
                            borderRadius="xl"
                            flexDirection="column"
                            alignItems="start"
                            p={4}
                        >
                            <Flex align="center" mb={3}>
                                <AlertIcon boxSize={5} />
                                <AlertTitle fontSize="sm">Esta estação não está enviando dados</AlertTitle>
                            </Flex>
                            <AlertDescription w="full">
                                {offlineSince && (
                                    <Box
                                        bg="orange.50"
                                        p={3}
                                        borderRadius="lg"
                                        mb={3}
                                    >
                                        <Text fontSize="sm" fontWeight="medium" color="orange.800">
                                            Offline desde: {offlineSince.format("DD/MM/YYYY [às] HH:mm")}
                                        </Text>
                                        <Text fontSize="xs" color="orange.600">
                                            {offlineSince.fromNow()}
                                        </Text>
                                    </Box>
                                )}
                                <Text fontSize="sm" color={subtleText}>
                                    Os dados exibidos podem estar desatualizados. A estação pode estar sem conexão com a internet ou com problemas técnicos.
                                </Text>
                            </AlertDescription>
                        </Alert>
                    </ModalBody>
                </ModalContent>
            </Modal>

            <Box
                bg={bgColor}
                borderRadius="2xl"
                shadow="xl"
                overflow="hidden"
                border="1px"
                borderColor={borderColor}
            >
                {/* Modern Station Header */}
                <Box
                    bg="linear-gradient(135deg, #2D6B3F 0%, #1F5A2F 100%)"
                    py={5}
                    px={6}
                >
                    <Flex align="center" justify="center" direction="column" gap={2}>
                        <HStack spacing={2} mb={1}>
                            <Badge
                                bg="whiteAlpha.200"
                                color="white"
                                px={2}
                                py={0.5}
                                borderRadius="full"
                                fontSize="xs"
                            >
                                {station?.provider}
                            </Badge>
                            <Badge
                                bg="whiteAlpha.200"
                                color="white"
                                px={2}
                                py={0.5}
                                borderRadius="full"
                                fontSize="xs"
                            >
                                {station?.identifier}
                            </Badge>
                        </HStack>
                        <Text
                            textAlign="center"
                            fontSize={{ base: "lg", md: "xl" }}
                            fontWeight="bold"
                            color="white"
                        >
                            {station?.full_name || `${station?.city} / ${station?.state}`}
                        </Text>
                        {station?.elevation && (
                            <Text fontSize="xs" color="whiteAlpha.800">
                                Altitude: {station.elevation}m
                            </Text>
                        )}
                    </Flex>
                </Box>

                <Box p={{ base: 3, md: 5 }}>
                {!!currData && (
                    <>
                        {/* Temperature Hero Section */}
                        <Flex
                            justify="center"
                            align="center"
                            gap={{ base: 3, md: 6 }}
                            mb={5}
                            flexWrap="wrap"
                        >
                            {/* Min Temperature */}
                            {extremos?.minima != null && (
                                <Box
                                    bg={minTempBg}
                                    px={{ base: 3, md: 5 }}
                                    py={{ base: 2, md: 3 }}
                                    borderRadius="xl"
                                    textAlign="center"
                                    border="1px"
                                    borderColor={minTempBorder}
                                >
                                    <Flex align="center" justify="center" gap={1} color={minTempLabel} mb={1}>
                                        <Icon as={TbArrowDown} boxSize={4} />
                                        <Text fontSize="xs" fontWeight="medium">Mínima</Text>
                                    </Flex>
                                    <Text
                                        fontSize={{ base: "xl", md: "2xl" }}
                                        fontWeight="bold"
                                        color={minTempValue}
                                    >
                                        {extremos?.minima ?? "-"}º
                                    </Text>
                                </Box>
                            )}

                            {/* Current Temperature */}
                            {currData?.temp != null && (
                                <Box
                                    bg={currTempBg}
                                    px={{ base: 5, md: 8 }}
                                    py={{ base: 3, md: 5 }}
                                    borderRadius="2xl"
                                    textAlign="center"
                                    border="2px"
                                    borderColor={currTempBorder}
                                    shadow="lg"
                                >
                                    <Text
                                        fontSize={{ base: "3xl", md: "5xl" }}
                                        fontWeight="bold"
                                        color={currTempValue}
                                        lineHeight="1"
                                    >
                                        {currData?.temp ?? "-"}º
                                    </Text>
                                    {currData?.heat_index && (
                                        <Text
                                            mt={1}
                                            fontSize="xs"
                                            color={subtleText}
                                        >
                                            Sensação de {currData?.heat_index ?? "-"}º
                                        </Text>
                                    )}
                                </Box>
                            )}

                            {/* Max Temperature */}
                            {extremos?.maxima != null && (
                                <Box
                                    bg={maxTempBg}
                                    px={{ base: 3, md: 5 }}
                                    py={{ base: 2, md: 3 }}
                                    borderRadius="xl"
                                    textAlign="center"
                                    border="1px"
                                    borderColor={maxTempBorder}
                                >
                                    <Flex align="center" justify="center" gap={1} color={maxTempLabel} mb={1}>
                                        <Text fontSize="xs" fontWeight="medium">Máxima</Text>
                                        <Icon as={TbArrowUp} boxSize={4} />
                                    </Flex>
                                    <Text
                                        fontSize={{ base: "xl", md: "2xl" }}
                                        fontWeight="bold"
                                        color={maxTempValue}
                                    >
                                        {extremos?.maxima ?? "-"}º
                                    </Text>
                                </Box>
                            )}
                        </Flex>

                        {/* Weather Data Cards Grid */}
                        <Flex
                            wrap="wrap"
                            gap={{ base: 3, md: 4 }}
                            justify="center"
                        >
                            {currData?.humidity != null && (
                                <WeatherDataCard
                                    icon={<TbDroplets />}
                                    color={"blue.400"}
                                    text={"Umidade"}
                                    data={`${currData?.humidity ?? "-"}%`}
                                />
                            )}

                            {currData?.wind_speed != null && (
                                <WeatherDataCard
                                    icon={<TbWind />}
                                    color={"green.400"}
                                    text={"Vel. do Vento"}
                                    data={`${currData?.wind_speed ?? "-"}km/h`}
                                />
                            )}

                            {currData?.wind_dir != null && (
                                <WeatherDataCard
                                    icon={<TbWindsock />}
                                    color={"red.400"}
                                    text={"Dir. do Vento"}
                                    data={`${currData.wind_dir ?? "-"}º (${degreesToCardinal(
                                        currData.wind_dir
                                    )})`}
                                />
                            )}

                            {currData?.wind_gust != null &&
                                <WeatherDataCard
                                    icon={<TbWindmill />}
                                    color={"purple.400"}
                                    text={"Raj. de Vento"}
                                    data={`${currData.wind_gust ?? "-"}km/h`}
                                />
                            }

                            {currData?.precip_rate != null && (
                                <WeatherDataCard
                                    icon={<TbCloudRain />}
                                    color={"blue.600"}
                                    text={"Índice de Chuva"}
                                    data={`${currData.precip_rate ?? "-"}mm`}
                                />
                            )}

                            {currData?.precip_total != null && (
                                <WeatherDataCard
                                    icon={<TbDropCircle />}
                                    color={"cyan.400"}
                                    text={"Chuva Acumulada"}
                                    data={`${currData.precip_total ?? "-"}mm`}
                                />
                            )}

                            {(currData?.solar_radiation != null || currData?.uv != null) && (
                                <WeatherDataCard
                                    icon={<TbSunHigh />}
                                    color={"yellow.400"}
                                    text={"Rad. Solar " + (currData.solar_radiation ?? '-')}
                                    data={`UV ${currData.uv ?? "-"}`}
                                />
                            )}

                            {currData?.pressure != null && (
                                <WeatherDataCard
                                    icon={<TbArrowBigDownLines />}
                                    color={"green.400"}
                                    text={"Pressão Atmosf."}
                                    data={`${currData.pressure ?? "-"}`}
                                />
                            )}

                            {currData?.eto != null && (
                                <WeatherDataCard
                                    icon={<TbLeaf />}
                                    color={"green.400"}
                                    text={"ETo"}
                                    data={currData?.eto ? `${Number(currData?.eto)?.toFixed(2)} mm` : "-"}
                                />
                            )}

                            {!!station?.battery_level && (
                                <WeatherDataCard
                                    icon={<TbBattery3 />}
                                    color={"green.400"}
                                    text={"Nível da Bateria"}
                                    data={`${station?.battery_level ?? "-"}`}
                                />
                            )}


                            {pulvData != null && (
                                <WeatherDataCard
                                    icon={<TbSpray />}
                                    color={"red.400"}
                                    text={"Pulverização"}
                                    data={
                                        <Flex direction={"column"} align={"center"} w="full" fontSize={"sm"}>
                                            <Text color={pulvData.status === "good" ? "whatsapp.500" : pulvData.status === "ok" ? "yellow.500" : "red.500"} fontWeight={"semibold"}>
                                                {pulvData.status === "good" ? "Ideal" : pulvData.status === "ok" ? "Arriscado" : "Inadequado"}
                                            </Text>
                                            <Flex direction={"row"} gap={3} fontSize={"xs"} mt={1}>
                                                <Flex whiteSpace={"nowrap"} gap={1} align="center" direction={"row"}>
                                                    <Icon fontSize={"md"} as={pulvData.deltaTStatus === "good" ? TbCircleCheckFilled : pulvData.deltaTStatus === "ok" ? TbAlertTriangleFilled : TbCircleXFilled} color={pulvData.deltaTStatus === "good" ? "whatsapp.500" : pulvData.deltaTStatus === "ok" ? "yellow.500" : "red.500"} />
                                                    <Text fontSize={"xs"}>Δ{pulvData.deltaT?.toFixed(1)}º</Text>
                                                </Flex>
                                                <Flex whiteSpace={"nowrap"} gap={1} align="center" direction={"row"}>
                                                    <Icon fontSize={"md"} as={pulvData.windStatus === "good" ? TbCircleCheckFilled : pulvData.windStatus === "ok" ? TbCircleDotFilled : TbCircleXFilled} color={pulvData.windStatus === "good" ? "whatsapp.500" : pulvData.windStatus === "ok" ? "yellow.500" : "red.500"} />
                                                    <Text fontSize={"xs"}>{pulvData.wind}km/h</Text>
                                                </Flex>
                                            </Flex>
                                        </Flex>
                                    }
                                />
                            )}

                            {currData?.vpd != null && (
                                <WeatherDataCard
                                    icon={<TbSteam />}
                                    color={"teal.400"}
                                    text={"VPD"}
                                    data={
                                        <Flex direction={"column"} align={"center"} w="full">
                                            <Text>{formatVpd(currData.vpd)} kPa</Text>
                                            {vpdExtremes && (
                                                <Flex direction={"row"} gap={3} mt={1} fontSize={"xs"} fontWeight={"normal"} color={subtleText}>
                                                    <Flex whiteSpace={"nowrap"} gap={0.5} align="center">
                                                        <Icon as={TbArrowDown} />
                                                        <Text>{formatVpd(vpdExtremes.min)}</Text>
                                                    </Flex>
                                                    <Flex whiteSpace={"nowrap"} gap={0.5} align="center">
                                                        <Icon as={TbArrowUp} />
                                                        <Text>{formatVpd(vpdExtremes.max)}</Text>
                                                    </Flex>
                                                </Flex>
                                            )}
                                        </Flex>
                                    }
                                />
                            )}

                            {fireRating != null && (
                                <WeatherDataCard
                                    icon={<TbFlame />}
                                    color={"red.500"}
                                    text={"Perigo de Fogo"}
                                    data={
                                        <Text color={fireRating.color + ".500"} fontWeight={"semibold"} fontSize="sm" textAlign="center">
                                            {fireRating.level}
                                        </Text>
                                    }
                                />
                            )}

                            {extraFields.length > 0 && currData?.extra && extraFields.map(field => (
                                currData.extra[field.field_path] != null && (
                                    <WeatherDataCard
                                        key={field.id}
                                        icon={<TbBroadcast />}
                                        color={"brand.500"}
                                        text={field.name}
                                        data={`${currData.extra[field.field_path]}${field.unit ? ` ${field.unit}` : ''}`}
                                    />
                                )
                            ))}
                        </Flex>

                        {/* Last Updated */}
                        {(currData.created_at || station.last_sync_at) && (
                            <Text textAlign="center" color={subtleText} fontSize="xs" mt={4}>
                                Atualizado {moment(currData.created_at || station.last_sync_at).format("llll")}
                            </Text>
                        )}
                    </>
                )}

                {currData?.temp != null && (
                    <Box
                        mt={6}
                        bg={cardBg}
                        borderRadius="xl"
                        border="1px"
                        borderColor={borderColor}
                        overflow="hidden"
                    >
                        <Box
                            bg="linear-gradient(135deg, #2D6B3F 0%, #1F5A2F 100%)"
                            px={4}
                            py={3}
                        >
                            <HStack spacing={2}>
                                <Icon as={TbChartLine} color="white" boxSize={5} />
                                <Text color="white" fontWeight="semibold" fontSize="md">
                                    Últimas 24 Horas - Temperatura
                                </Text>
                            </HStack>
                        </Box>
                        <Box p={4}>
                            {data24.length > 0 && (
                                <ReactApexChart
                                    type="line"
                                    options={getChartOptions24("°")}
                                    series={getChartSeries24Temp()}
                                    width="100%"
                                    height={350}
                                />
                            )}
                            {station?.provider === "INMET" && (
                                <Text textAlign="center" fontSize="xs" color={subtleText} mt={2}>
                                    Fonte de dados: INMET
                                </Text>
                            )}
                        </Box>
                    </Box>
                )}

                {hasVpd24 && (
                    <Box
                        mt={4}
                        bg={cardBg}
                        borderRadius="xl"
                        border="1px"
                        borderColor={borderColor}
                        overflow="hidden"
                    >
                        <Box
                            bg="linear-gradient(135deg, #319795 0%, #2C7A7B 100%)"
                            px={4}
                            py={3}
                        >
                            <HStack spacing={2}>
                                <Icon as={TbSteam} color="white" boxSize={5} />
                                <Text color="white" fontWeight="semibold" fontSize="md">
                                    Últimas 24 Horas - VPD
                                </Text>
                            </HStack>
                        </Box>
                        <Box p={4}>
                            <ReactApexChart
                                type="line"
                                options={getChartOptions24(" kPa")}
                                series={getChartSeries24Vpd()}
                                width="100%"
                                height={350}
                            />
                        </Box>
                    </Box>
                )}

                {currData?.precip_rate != null && (
                    <Box
                        mt={4}
                        bg={cardBg}
                        borderRadius="xl"
                        border="1px"
                        borderColor={borderColor}
                        overflow="hidden"
                    >
                        <Box
                            bg="linear-gradient(135deg, #3182CE 0%, #2B6CB0 100%)"
                            px={4}
                            py={3}
                        >
                            <HStack spacing={2}>
                                <Icon as={TbCloudRain} color="white" boxSize={5} />
                                <Text color="white" fontWeight="semibold" fontSize="md">
                                    Últimas 24 Horas - Chuva
                                </Text>
                            </HStack>
                        </Box>
                        <Box p={4}>
                            {data24.length > 0 && (
                                <ReactApexChart
                                    options={getChartOptions24("mm", false)}
                                    series={getChartSeries24Precip()}
                                    width="100%"
                                    height={350}
                                    type="bar"
                                />
                            )}
                            {station?.provider === "INMET" && (
                                <Text textAlign="center" fontSize="xs" color={subtleText} mt={2}>
                                    Fonte de dados: INMET
                                </Text>
                            )}
                        </Box>
                    </Box>
                )}

                {currData?.temp != null && (
                    <Box
                        mt={4}
                        bg={cardBg}
                        borderRadius="xl"
                        border="1px"
                        borderColor={borderColor}
                        overflow="hidden"
                    >
                        <Box
                            bg="linear-gradient(135deg, #DD6B20 0%, #C05621 100%)"
                            px={4}
                            py={3}
                        >
                            <HStack spacing={2}>
                                <Icon as={TbTemperature} color="white" boxSize={5} />
                                <Text color="white" fontWeight="semibold" fontSize="md">
                                    Últimos 7 Dias - Temperatura
                                </Text>
                            </HStack>
                        </Box>
                        <Box p={4}>
                            {data.length > 0 && (
                                <ReactApexChart
                                    type="line"
                                    options={getChartOptions("°")}
                                    series={getChartSeriesTemp()}
                                    width="100%"
                                    height={350}
                                />
                            )}
                            {station?.provider === "INMET" && (
                                <Text textAlign="center" fontSize="xs" color={subtleText} mt={2}>
                                    Fonte de dados: INMET
                                </Text>
                            )}
                        </Box>
                    </Box>
                )}

                {currData?.precip_rate != null && (
                    <Box
                        mt={4}
                        bg={cardBg}
                        borderRadius="xl"
                        border="1px"
                        borderColor={borderColor}
                        overflow="hidden"
                    >
                        <Box
                            bg="linear-gradient(135deg, #00B5D8 0%, #0987A0 100%)"
                            px={4}
                            py={3}
                        >
                            <HStack spacing={2}>
                                <Icon as={TbDropCircle} color="white" boxSize={5} />
                                <Text color="white" fontWeight="semibold" fontSize="md">
                                    Últimos 7 Dias - Chuva
                                </Text>
                            </HStack>
                        </Box>
                        <Box p={4}>
                            {data.length > 0 && (
                                <ReactApexChart
                                    options={getChartOptions("mm")}
                                    series={getChartSeriesPrecip()}
                                    width="100%"
                                    height={350}
                                    type="bar"
                                />
                            )}
                            {station?.provider === "INMET" && (
                                <Text textAlign="center" fontSize="xs" color={subtleText} mt={2}>
                                    Fonte de dados: INMET
                                </Text>
                            )}
                        </Box>
                    </Box>
                )}

                {/* Extra Field Charts */}
                {extraData24.length > 0 && extraData24.map(fieldData => {
                    const suffix = fieldData.unit ? ` ${fieldData.unit}` : "";
                    return (
                        <Box
                            key={`extra-24h-${fieldData.field_id}`}
                            mt={4}
                            bg={cardBg}
                            borderRadius="xl"
                            border="1px"
                            borderColor={borderColor}
                            overflow="hidden"
                        >
                            <Box
                                bg="linear-gradient(135deg, #2D6B3F 0%, #1F5A2F 100%)"
                                px={4}
                                py={3}
                            >
                                <HStack spacing={2}>
                                    <Icon as={TbBroadcast} color="white" boxSize={5} />
                                    <Text color="white" fontWeight="semibold" fontSize="md">
                                        Últimas 24 Horas - {fieldData.name}
                                    </Text>
                                </HStack>
                            </Box>
                            <Box p={4}>
                                <ReactApexChart
                                    type="line"
                                    options={{
                                        chart: {
                                            toolbar: { show: false },
                                            locales: [jsonData],
                                            defaultLocale: "pt-br",
                                        },
                                        tooltip: {
                                            theme: "dark",
                                            x: {
                                                show: true,
                                                format: "dd MMM HH:mm",
                                            },
                                            y: {
                                                formatter: (val) => val != null ? val + suffix : '',
                                            },
                                        },
                                        dataLabels: {
                                            enabled: true,
                                            formatter: function (val, opts) {
                                                if (opts.dataPointIndex === 0 || opts.dataPointIndex % 20 !== 0)
                                                    return "";
                                                return val + suffix;
                                            },
                                        },
                                        xaxis: {
                                            type: "datetime",
                                            categories: fieldData.data.map(d => d.date),
                                            show: true,
                                            color: "#A0AEC0",
                                            labels: {
                                                show: true,
                                                datetimeUTC: false,
                                                style: {
                                                    colors: "#A0AEC0",
                                                    fontSize: "14px",
                                                },
                                            },
                                        },
                                        yaxis: {
                                            show: true,
                                            color: "#A0AEC0",
                                            labels: {
                                                show: true,
                                                style: {
                                                    colors: "#A0AEC0",
                                                    fontSize: "14px",
                                                },
                                            },
                                        },
                                    }}
                                    series={[{ name: fieldData.name, data: fieldData.data.map(d => d.value), color: "#2D6B3F" }]}
                                    width="100%"
                                    height={350}
                                />
                            </Box>
                        </Box>
                    );
                })}

                {extraData7d.length > 0 && extraData7d.map(fieldData => {
                    const suffix = fieldData.unit ? ` ${fieldData.unit}` : "";
                    return (
                        <Box
                            key={`extra-7d-${fieldData.field_id}`}
                            mt={4}
                            bg={cardBg}
                            borderRadius="xl"
                            border="1px"
                            borderColor={borderColor}
                            overflow="hidden"
                        >
                            <Box
                                bg="linear-gradient(135deg, #2D6B3F 0%, #1F5A2F 100%)"
                                px={4}
                                py={3}
                            >
                                <HStack spacing={2}>
                                    <Icon as={TbBroadcast} color="white" boxSize={5} />
                                    <Text color="white" fontWeight="semibold" fontSize="md">
                                        Últimos 7 dias - {fieldData.name}
                                    </Text>
                                </HStack>
                            </Box>
                            <Box p={4}>
                                <ReactApexChart
                                    type="line"
                                    options={{
                                        chart: {
                                            toolbar: { show: false },
                                            locales: [jsonData],
                                            defaultLocale: "pt-br",
                                        },
                                        tooltip: {
                                            theme: "dark",
                                            x: {
                                                show: true,
                                            },
                                            y: {
                                                formatter: (val) => val != null ? val + suffix : '',
                                            },
                                        },
                                        dataLabels: {
                                            enabled: true,
                                            formatter: function (val) {
                                                return val + suffix;
                                            },
                                        },
                                        xaxis: {
                                            type: "datetime",
                                            categories: fieldData.data.map(d => d.date),
                                            show: true,
                                            color: "#A0AEC0",
                                            labels: {
                                                show: true,
                                                datetimeUTC: true,
                                                style: {
                                                    colors: "#A0AEC0",
                                                    fontSize: "14px",
                                                },
                                            },
                                        },
                                        yaxis: {
                                            show: true,
                                            color: "#A0AEC0",
                                            labels: {
                                                show: true,
                                                style: {
                                                    colors: "#A0AEC0",
                                                    fontSize: "14px",
                                                },
                                            },
                                        },
                                    }}
                                    series={[
                                        { name: "Mínima", data: fieldData.data.map(d => d.min_value), color: "#3182CE" },
                                        { name: "Média", data: fieldData.data.map(d => d.avg_value), color: "#2D6B3F" },
                                        { name: "Máxima", data: fieldData.data.map(d => d.max_value), color: "#E53E3E" },
                                    ]}
                                    width="100%"
                                    height={350}
                                />
                            </Box>
                        </Box>
                    );
                })}

                {/* Forecast Section */}
                <Box
                    mt={4}
                    bg={cardBg}
                    borderRadius="xl"
                    border="1px"
                    borderColor={borderColor}
                    overflow="hidden"
                >
                    <Box
                        bg="linear-gradient(135deg, #805AD5 0%, #6B46C1 100%)"
                        px={4}
                        py={3}
                    >
                        <HStack spacing={2}>
                            <Icon as={TbCloudQuestion} color="white" boxSize={5} />
                            <Text color="white" fontWeight="semibold" fontSize="md">
                                Previsão do Tempo
                            </Text>
                        </HStack>
                    </Box>
                    <Box p={4}>
                        <Forecast16Graphs station_id={id} />
                    </Box>
                </Box>

                {/* Station Map */}
                {!!station && (
                    <Box
                        mt={4}
                        bg={cardBg}
                        borderRadius="xl"
                        border="1px"
                        borderColor={borderColor}
                        overflow="hidden"
                    >
                        <Box
                            bg="linear-gradient(135deg, #38A169 0%, #2F855A 100%)"
                            px={4}
                            py={3}
                        >
                            <HStack spacing={2}>
                                <Icon as={TbMapPin} color="white" boxSize={5} />
                                <Text color="white" fontWeight="semibold" fontSize="md">
                                    Localização da Estação
                                </Text>
                            </HStack>
                        </Box>
                        <Box p={0}>
                            <StationMap station={station} />
                        </Box>
                    </Box>
                )}

                {/* Data Table Section */}
                <Box
                    mt={4}
                    bg={cardBg}
                    borderRadius="xl"
                    border="1px"
                    borderColor={borderColor}
                    overflow="hidden"
                >
                    <Box
                        bg="linear-gradient(135deg, #718096 0%, #4A5568 100%)"
                        px={4}
                        py={3}
                    >
                        <HStack spacing={2}>
                            <Icon as={TbTable} color="white" boxSize={5} />
                            <Text color="white" fontWeight="semibold" fontSize="md">
                                Últimos 10 dados recebidos
                            </Text>
                        </HStack>
                    </Box>
                    <Box p={4} overflowX="auto">
                        <Table variant="simple" size="sm">
                            <Thead bg={tableHeaderBg}>
                                <Tr>
                                    <Th borderRadius="md 0 0 md">Momento</Th>
                                    <Th>Temperatura</Th>
                                    <Th>Umidade</Th>
                                    <Th>Chuva</Th>
                                    <Th>Vento</Th>
                                    <Th>Índices Solares</Th>
                                    <Th borderRadius="0 md md 0">Pressão</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {last10Data?.length > 0 ? (
                                    last10Data?.map((_data) => (
                                        <Tr>
                                            <Td>{moment(_data.created_at).format("lll")}</Td>
                                            <Td>{_data.temp ?? "-"}ºC</Td>
                                            <Td>{_data.humidity ?? "-"}%</Td>
                                            <Td>
                                                <b>Índice:</b> {_data.precip_rate ?? "-"}mm
                                                <br />
                                                <b>Acumulado:</b> {_data.precip_total ?? "-"}mm
                                            </Td>
                                            <Td>
                                                <b>Velocidade:</b> {_data.wind_speed ?? "-"}km/h
                                                <br />
                                                <b>Direção:</b> {_data.wind_dir ?? "-"}º (
                                                {degreesToCardinal(currData.wind_dir)})
                                                <br />
                                                <b>Rajada:</b> {_data.wind_gust ?? "-"}km/h
                                            </Td>
                                            <Td>
                                                <b>UV:</b> {currData.uv ?? "-"} <br /> <b>Radiação:</b>{" "}
                                                {currData.solar_radiation ?? "-"}
                                            </Td>
                                            <Td>{_data.pressure ?? "-"}</Td>
                                        </Tr>
                                    ))
                                ) : (
                                    <>
                                        <Tr>
                                            <Td colSpan={99}>
                                                <Skeleton w={"full"} h="20px" />
                                            </Td>
                                        </Tr>
                                        <Tr>
                                            <Td colSpan={99}>
                                                <Skeleton w={"full"} h="20px" />
                                            </Td>
                                        </Tr>
                                        <Tr>
                                            <Td colSpan={99}>
                                                <Skeleton w={"full"} h="20px" />
                                            </Td>
                                        </Tr>
                                    </>
                                )}
                            </Tbody>
                        </Table>
                    </Box>
                </Box>
                </Box>
            </Box>
        </Box>
    );
}

function formatVpd(vpd) {
    return vpd == null ? "-" : Number(vpd).toFixed(2);
}

function calculateDewPoint(temperature, humidity) {
    var a = 17.27;
    var b = 237.3;
    var gamma = (a * temperature) / (b + temperature) + Math.log(humidity / 100);

    var dewPoint = (b * gamma) / (a - gamma);

    return dewPoint;
}

function calculatePulv(currData) {
    if (currData?.temp == null || currData?.humidity == null || currData?.wind_speed == null) return null;

    let temp = parseFloat(currData.temp ?? 0);
    let humidity = parseFloat(currData.humidity ?? 0);
    let wind = parseFloat(currData.wind_speed ?? 0);

    let dewPoint = currData.dew_point ? parseFloat(currData.dew_point) : calculateDewPoint(temp, humidity);


    let deltaT = temp - dewPoint;
    let deltaTStatus = "good";

    if (deltaT >= 3 && deltaT <= 8) {
        deltaTStatus = "good";
    } else if (
        (deltaT >= 0 && deltaT < 3)
        ||
        (deltaT > 8 && deltaT <= 10)
    ) {
        deltaTStatus = "ok";
    } else {
        deltaTStatus = "bad";
    }

    let windStatus = "good";
    if (wind >= 2 && wind <= 12) {
        windStatus = "good";
    } else if (wind > 12 && wind <= 15) {
        windStatus = "ok";
    } else {
        windStatus = "bad";
    }

    let status = "good";
    switch (deltaTStatus) {
        case "good":
            switch (windStatus) {
                case "good":
                    status = "good";
                    break;
                case "ok":
                    status = "ok";
                    break;
                case "bad":
                    status = "bad";
                    break;
            }
            break;
        case "ok":
            switch (windStatus) {
                case "good":
                case "ok":
                    status = "ok";
                    break;
                case "bad":
                    status = "bad";
                    break;
            }
            break;
        case "bad":
            status = "bad";
            break;
    }

    return {
        deltaT,
        deltaTStatus,
        wind,
        windStatus,
        status
    }
}

function WeatherDataCard({ icon, color, text, data }) {
    const cardBg = useColorModeValue("white", "gray.700");
    const borderCol = useColorModeValue("gray.200", "gray.600");
    const labelColor = useColorModeValue("gray.500", "gray.400");
    const valueColor = useColorModeValue("gray.800", "white");
    const shadowBase = useColorModeValue(
        "0 2px 8px rgba(0, 0, 0, 0.08)",
        "0 2px 8px rgba(0, 0, 0, 0.3)"
    );
    const shadowHover = useColorModeValue(
        "0 4px 12px rgba(0, 0, 0, 0.12)",
        "0 4px 12px rgba(0, 0, 0, 0.4)"
    );

    return (
        <Box
            bg={cardBg}
            borderRadius="xl"
            overflow="hidden"
            transition="all 0.2s ease"
            boxShadow={shadowBase}
            border="1px solid"
            borderColor={borderCol}
            w={{ base: "calc(50% - 6px)", sm: "140px", md: "150px" }}
            minW={{ base: "auto", sm: "140px" }}
            _hover={{
                transform: "translateY(-2px)",
                boxShadow: shadowHover,
            }}
        >
            {/* Colored Top Accent Bar */}
            <Box h="4px" bg={color} w="full" />

            <Flex direction="column" align="center" p={4} gap={2}>
                {/* Icon */}
                <Flex
                    align="center"
                    justify="center"
                    color={color}
                    fontSize="3xl"
                >
                    {icon}
                </Flex>

                {/* Label */}
                <Text
                    fontSize="xs"
                    color={labelColor}
                    fontWeight="medium"
                    textAlign="center"
                    noOfLines={1}
                >
                    {text}
                </Text>

                {/* Value */}
                <Box
                    fontSize="md"
                    fontWeight="bold"
                    color={valueColor}
                    textAlign="center"
                    lineHeight="1.3"
                >
                    {data}
                </Box>
            </Flex>
        </Box>
    );
}
